import React, { useState } from "react";

// Deterministic initials-avatar. Color is derived from a lightweight
// FNV-1a hash of the user_id (or email fallback) so the same user
// always gets the same color across every card in the marketplace,
// but different users spread across the palette.
//
// Why this palette: hand-picked WCAG AA contrast against white text at
// the sizes we use (24-96px). No blue-on-blue collisions. No grey (grey
// looks broken, like a missing image). Order matters for hash-modulo.
const PALETTE = [
    "#0ea5e9", // sky-500
    "#8b5cf6", // violet-500
    "#f59e0b", // amber-500
    "#10b981", // emerald-500
    "#ef4444", // rose-500 (kept subtle — not for errors here)
    "#6366f1", // indigo-500
    "#14b8a6", // teal-500
    "#f97316", // orange-500
    "#a855f7", // purple-500
    "#22c55e", // green-500
    "#eab308", // yellow-500
    "#3b82f6", // blue-500
];

const hashString = (s) => {
    // FNV-1a 32-bit — fast, well-distributed, no dependency.
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
};

const initialsOf = (name, email) => {
    const raw = (name || "").trim();
    if (raw) {
        const parts = raw.split(/\s+/).filter(Boolean).slice(0, 2);
        return parts.map((p) => p[0]).join("").toUpperCase() || "?";
    }
    // Fall back to email's local-part first char if the name is missing.
    const em = (email || "").trim();
    return em ? em[0].toUpperCase() : "?";
};

/**
 * Reusable avatar. Renders a real <img> when ``url`` is present,
 * otherwise a deterministic initials tile so no card ever shows a
 * broken image placeholder.
 *
 * Props:
 *   url    — image URL (optional)
 *   name   — full name (used for initials + hash if no user_id)
 *   email  — email (used for initials + hash fallback)
 *   userId — canonical user_id (preferred hash source; same user →
 *            same color across every surface)
 *   initials — explicit initials override (for anonymized surfaces
 *              where the caller already computed them, e.g. the
 *              anonymized Talent Marketplace)
 *   size   — tile size in px (default 40)
 *   ring   — whether to render a subtle border ring (default true)
 *   className — extra classes to layer on
 *   testId — data-testid override
 */
export const Avatar = ({
    url, name, email, userId,
    initials: initialsOverride,
    size = 40,
    ring = true,
    className = "",
    testId,
}) => {
    const hashSource = String(userId || email || name || "unknown");
    const color = PALETTE[hashString(hashSource) % PALETTE.length];
    const initials = initialsOverride || initialsOf(name, email);
    const commonClasses = `inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${ring ? "border border-border" : ""} ${className}`;
    const style = { width: size, height: size };

    if (url) {
        return <AvatarImage
            url={url}
            alt={name || email || "avatar"}
            size={size}
            commonClasses={commonClasses}
            style={style}
            testId={testId || "avatar-image"}
            shimmerColor={color}
        />;
    }
    // Font size scales with tile size — ~40% keeps initials readable at
    // both 24px thumbnails and 96px profile cards.
    const fontSize = Math.max(10, Math.round(size * 0.4));
    return (
        <span
            data-testid={testId || "avatar-initials"}
            className={commonClasses}
            style={{ ...style, backgroundColor: color, color: "white", fontSize, fontWeight: 600 }}
            title={name || email || ""}
        >
            {initials}
        </span>
    );
};

// Handles the "photo is loading" state — a subtle pulsing tile in the
// user's deterministic color acts as a placeholder until the browser
// finishes fetching + decoding the image. Feels snappier than a
// half-drawn image on slow connections. Errors fall back to the
// initials tile (rendered by the parent).
const AvatarImage = ({ url, alt, size, commonClasses, style, testId, shimmerColor }) => {
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    // Font-size fallback if the img errors — same rule as the initials tile.
    const fontSize = Math.max(10, Math.round(size * 0.4));
    if (errored) {
        // First char of alt (name or email) as the fallback initial.
        const c = (alt || "?").trim()[0]?.toUpperCase() || "?";
        return (
            <span
                data-testid={testId + "-errored"}
                className={commonClasses}
                style={{ ...style, backgroundColor: shimmerColor, color: "white", fontSize, fontWeight: 600 }}
                title={alt}
            >
                {c}
            </span>
        );
    }
    return (
        <span
            data-testid={testId}
            className={`${commonClasses} relative`}
            style={style}
        >
            {!loaded && (
                <span
                    aria-hidden
                    data-testid={testId + "-shimmer"}
                    className="absolute inset-0 animate-pulse"
                    style={{ backgroundColor: shimmerColor, opacity: 0.35 }}
                />
            )}
            <img
                src={url}
                alt={alt}
                onLoad={() => setLoaded(true)}
                onError={() => setErrored(true)}
                className="w-full h-full object-cover"
                style={{ opacity: loaded ? 1 : 0, transition: "opacity 200ms ease-in-out" }}
            />
        </span>
    );
};

export default Avatar;
